// Copyright (c) Move Closer 2022.
import { DocumentNode } from 'graphql'
import gql from 'graphql-tag'

import { MagentoVersion } from '../../../../contexts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const CART_FRAGMENT: DocumentNode = gql`
  fragment CartFragment on Cart {
    applied_coupons {
      code
    }
    selected_payment_method_id
    available_payment_methods {
      code
      title
    }
    billing_address {
      city
      company
      country {
        code
        label
      }
      firstname
      lastname
      postcode
      region {
        code
        label
        region_id
      }
      street
      telephone
      vat_id
    }
    email
    id
    is_virtual
    items {
      __typename
      uid
      quantity
      prices {
        price {
          value
          currency
        }
        total_item_discount {
          value
          currency
        }
        row_total_including_tax {
          value
          currency
        }
      }
      product {
        sku
      }
      ... on ConfigurableCartItem {
        configured_variant {
          sku
          color
          color_pantone
          uid
        }
        configurable_options {
          configurable_product_option_value_uid
          value_id
          value_label
        }
      }
    }
    prices {
      applied_taxes {
        amount {
          value
          currency
        }
        label
      }
      grand_total {
        value
        currency
      }
      subtotal_including_tax {
        value
        currency
      }
      subtotal_excluding_tax {
        value
        currency
      }
      discounts {
        label
        amount {
          value
          currency
        }
      }
    }
    selected_payment_method {
      code
      title
    }
    selected_payment_method_info {
      payment_label
    }
    shipping_addresses {
      available_shipping_methods {
        amount {
          value
          currency
        }
        available
        carrier_code
        carrier_title
        error_message
        method_code
        method_title
      }
      city
      company
      country {
        code
        label
      }
      customer_notes
      firstname
      lastname
      pickup_location_code
      postcode
      region {
        code
        label
        region_id
      }
      selected_shipping_method {
        amount {
          value
          currency
        }
        carrier_code
        carrier_title
        method_code
        method_title
        delivery_time {
          deliveryFrom
          deliveryTo
        }
        disabled_payment_methods
        display_info
      }
      street
      telephone
    }
  }
`

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const CART_FRAGMENT_2_4: DocumentNode = gql`
  fragment CartFragment on Cart {
    applied_am_gift_cards {
        applied_balance {
            currency
            value
        }
        code
        current_balance {
            currency
            value
        }
        expiration_date
    }
    applied_coupons {
      code
    }
    selected_payment_method_id
    available_payment_methods {
      code
      title
    }
    billing_address {
      city
      company
      country {
        code
        label
      }
      firstname
      lastname
      postcode
      region {
        code
        label
        region_id
      }
      street
      telephone
      vat_id
    }
    email
    flight_number
    is_signup_requested
    id
    is_virtual
    items {
      __typename
      uid
      quantity
      prices {
        price {
          value
          currency
        }
        total_item_discount {
          value
          currency
        }
        row_total_including_tax {
          value
          currency
        }
      }
      product {
        sku
        name
        price { regularPrice { amount { value currency } } }
        stock_status
        only_x_left_in_stock
      }
      ... on ConfigurableCartItem {
        configured_variant {
          sku
          color
          uid
        }
        configurable_options {
          configurable_product_option_value_uid
          value_id
          value_label
        }
      }
    }
    prices {
      applied_taxes {
        amount {
          value
          currency
        }
        label
      }
      grand_total {
        value
        currency
      }
      subtotal_including_tax {
        value
        currency
      }
      subtotal_excluding_tax {
        value
        currency
      }
      discounts {
        label
        amount {
          value
          currency
        }
      }
    }
    selected_address_id
    selected_payment_method {
      code
      title
    }
    shipping_addresses {
      available_shipping_methods {
        price_incl_tax {
          value
          currency
        }
        available
        carrier_code
        carrier_title
        error_message
        method_code
        method_title
      }
      city
      company
      country {
        code
        label
      }
      customer_notes
      firstname
      lastname
      pickup_location_code
      postcode
      region {
        code
        label
        region_id
      }
      selected_shipping_method {
        amount {
          value
          currency
        }
        carrier_code
        carrier_title
        method_code
        method_title
        delivery_time {
            deliveryFrom
            deliveryTo
        }
        disabled_payment_methods
        display_info
      }
      street
      telephone
    }
  }
`

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const PRODUCT_FRAGMENT: DocumentNode = gql`
  fragment ProductFragment on ProductInterface {
    __typename
    name
    sku
    url_key
    stock_status
    categories {
      name
    }
    image {
      url
    }
    small_image {
      url
    }
    media_gallery {
      url
    }
    price_range {
      maximum_price {
        final_price {
          value
          currency
        }
        regular_price {
          value
          currency
        }
        discount {
          amount_off
          percent_off
        }
      }
    }
    visible_in_cart
    ... on ConfigurableProduct {
      variants {
        product {
          stock_status
          sku
          color
          color_pantone
        }
        attributes {
          uid
          code
          label
          value_index
        }
      }
    }
  }
`

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export const CART_FRAGMENTS_MAP: Record<MagentoVersion, DocumentNode> = {
  [MagentoVersion.Default]: CART_FRAGMENT,
  [MagentoVersion.Ver24]: CART_FRAGMENT_2_4
}
